import { Form, Input, Modal, Select, Space, Typography, message } from '@pankod/refine-antd'
import { useCreate, useCreateMany, useGetIdentity, useList } from '@pankod/refine-core'
import { useEffect, useMemo, useState } from 'react'
import paperTemplateMetaData from 'metadata/paperTemplate'
import paperTemplateSubSectionsMetaData from 'metadata/paperTemplateSubSections'
import { IPaperTemplate, IPaperTemplateSubSection } from 'interfaces'

type Props = {
  visible: boolean
  onClose: () => void
  onCreated?: () => Promise<void> | void
}

type TemplateOption = {
  id: string
  name: string
  productId?: string
  testTrackId?: string
  productType?: string
  questionCount: number
}

const REQUIRED_TOTAL = 75
const REQUIRED_SKT = 40
const REQUIRED_OTHER = 20
const REQUIRED_TQ = 15

export const CombinePaperTemplate: React.FC<Props> = ({ visible, onClose, onCreated }) => {
  const [form] = Form.useForm()
  const { data: user } = useGetIdentity()
  const { mutateAsync: createTemplate } = useCreate()
  const { mutateAsync: createSubsections } = useCreateMany()

  const { data: templatesList, isLoading: loadingTemplates } = useList<IPaperTemplate>({
    resource: 'paper_templates',
    metaData: {
      fields: [
        'id',
        'name',
        'active',
        'product_id',
        'test_track_id',
        { product: ['id', 'name', 'type'] },
      ],
    },
    config: {
      filters: [{ field: 'active', operator: 'eq', value: true }],
      pagination: { pageSize: 10000, current: 1 },
    },
    queryOptions: { enabled: visible },
  })

  const templateIds = useMemo(() => (templatesList?.data ?? []).map(t => t.id), [templatesList?.data])

  const { data: subSectionsList, isLoading: loadingSubsections } = useList<IPaperTemplateSubSection>({
    resource: 'paper_template_sub_sections',
    metaData: {
      fields: [
        'id',
        'name',
        'paper_template_id',
        'section_id',
        'subject_id',
        'theme_id',
        'sub_theme_id',
        'topic_id',
        'no_of_questions',
        'mark_per_question',
        'question_type_id',
        'report_theme_id',
        'blooms_level_id',
        'difficult_level_id',
        'competency_id',
        'strand_id',
        'base_lang',
        'target_lang',
      ],
    },
    config: {
      filters: templateIds.length
        ? [{ field: 'paper_template_id', operator: 'in', value: templateIds }]
        : [],
      pagination: { pageSize: 100000, current: 1 },
    },
    queryOptions: { enabled: visible && templateIds.length > 0 },
  })

  const questionCountByTemplateId = useMemo(() => {
    const map = new Map<string, number>()
    ;(subSectionsList?.data ?? []).forEach(ss => {
      const prev = map.get(ss.paper_template_id) ?? 0
      // API can return numeric fields as strings; always coerce to number.
      const n = Number((ss as any).no_of_questions ?? 0)
      map.set(ss.paper_template_id, prev + (Number.isFinite(n) ? n : 0))
    })
    return map
  }, [subSectionsList?.data])

  const options = useMemo<TemplateOption[]>(() => {
    return (templatesList?.data ?? []).map(t => ({
      id: t.id,
      name: t.name,
      productId: t.product_id,
      testTrackId: t.test_track_id,
      productType: (t as any)?.product?.type,
      questionCount: Number(questionCountByTemplateId.get(t.id) ?? 0),
    }))
  }, [templatesList?.data, questionCountByTemplateId])

  function getOption(id?: string) {
    return options.find(o => o.id === id)
  }

  const tqId = Form.useWatch('tq_paper_template_id', form)
  const sktId = Form.useWatch('skt_paper_template_id', form)
  const otherId = Form.useWatch('other_paper_template_id', form)

  const excludeOtherSelected = (opts: TemplateOption[], selfId?: string) => {
    const selected = new Set([tqId, sktId, otherId].filter(Boolean) as string[])
    if (selfId) selected.delete(selfId)
    if (selected.size === 0) return opts
    return opts.filter(o => !selected.has(o.id))
  }

  const base = useMemo(() => getOption(tqId), [tqId, options])
  const sameCombo = (o: TemplateOption) => {
    if (!base) return true
    return o.productId === base.productId && o.testTrackId === base.testTrackId
  }

  const tqOptions = useMemo(() => {
    const opts = options.filter(o => o.questionCount === REQUIRED_TQ)
    return excludeOtherSelected(opts, tqId)
  }, [options, tqId, sktId, otherId])

  /**
   * Don't hide templates when counts mismatch; show them disabled.
   * This makes it obvious whether the issue is \"no template with exactly 40Q\" vs \"data not loading\".
   */
  const sktOptions = useMemo(() => {
    const opts = options.filter(sameCombo)
    return excludeOtherSelected(opts, sktId)
  }, [options, base?.productId, base?.testTrackId, tqId, sktId, otherId])

  const otherOptions = useMemo(() => {
    const opts = options.filter(sameCombo)
    return excludeOtherSelected(opts, otherId)
  }, [options, base?.productId, base?.testTrackId, tqId, sktId, otherId])

  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!visible) {
      form.resetFields()
      setSubmitting(false)
    }
  }, [visible, form])

  const validateSelection = (tqId?: string, sktId?: string, otherId?: string) => {
    if (!tqId || !sktId || !otherId) return { ok: false, message: 'Please select all 3 paper templates.' }
    if (new Set([tqId, sktId, otherId]).size !== 3) return { ok: false, message: 'Please select 3 different paper templates.' }

    const tq = getOption(tqId) 
    const skt = getOption(sktId)
    const other = getOption(otherId)
    if (!tq || !skt || !other) return { ok: false, message: 'Invalid paper template selection.' }

    const total = tq.questionCount + skt.questionCount + other.questionCount
    if (tq.questionCount !== REQUIRED_TQ) {
      return { ok: false, message: `TQ template must have exactly ${REQUIRED_TQ} questions.` }
    }
    if (skt.questionCount !== REQUIRED_SKT) {
      return { ok: false, message: `SKT template must have exactly ${REQUIRED_SKT} questions.` }
    }
    if (other.questionCount !== REQUIRED_OTHER) {
      return { ok: false, message: `Other template must have exactly ${REQUIRED_OTHER} questions.` }
    }
    if (total !== REQUIRED_TOTAL) {
      return { ok: false, message: `Total questions must be exactly ${REQUIRED_TOTAL}. Currently ${total}.` }
    }

    if (tq.productId !== skt.productId || skt.productId !== other.productId) {
      return { ok: false, message: 'All three templates must use the same product.' }
    }
    if (tq.testTrackId !== skt.testTrackId || skt.testTrackId !== other.testTrackId) {
      return { ok: false, message: 'All three templates must use the same test track.' }
    }

    return { ok: true, tq, skt, other, total }
  }

  const handleCreate = async () => {
    const values = await form.validateFields()
    const tqId = values?.tq_paper_template_id
    const sktId = values?.skt_paper_template_id
    const otherId = values?.other_paper_template_id

    const validation = validateSelection(tqId, sktId, otherId)
    if (!validation.ok) {
      message.error(validation.message)
      return
    }

    const { tq, skt, other } = validation as any

    const sourceOrder = [skt.id, tq.id, other.id]
    const selectedSubsections = (subSectionsList?.data ?? [])
      .filter(ss => [tq.id, skt.id, other.id].includes(ss.paper_template_id))
      .sort((a, b) => {
        const ia = sourceOrder.indexOf(a.paper_template_id)
        const ib = sourceOrder.indexOf(b.paper_template_id)
        if (ia !== ib) return ia - ib
        return (a.name || '').localeCompare(b.name || '')
      })
    if (selectedSubsections.length === 0) {
      message.error('Unable to find subsections for selected templates. Wait for data to load or check templates have subsection rows.')
      return
    }

    setSubmitting(true)
    try {
      const created: any = await createTemplate({
        resource: 'paper_templates',
        values: {
          name: values?.name,
          description: values?.description,
          product_id: tq.productId,
          test_track_id: tq.testTrackId,
          status: '',
          sections: Array.from(new Set(selectedSubsections.map(s => s.section_id).filter(Boolean))),
          created_by: user?.id,
          is_completed: true,
          active: true,
        },
        metaData: paperTemplateMetaData,
      })

      const newTemplateId =
        created?.data?.id ?? created?.data?.data?.id ?? (created as any)?.id ?? (created as any)?.data?.object?.id
      if (!newTemplateId) {
        message.error('Failed to create combined paper template (could not read new id from server response).')
        return
      }

      const subsectionValues = selectedSubsections.map(ss => ({
        name: ss.name,
        paper_template_id: newTemplateId,
        section_id: ss.section_id,
        subject_id: ss.subject_id,
        theme_id: ss.theme_id ?? null,
        sub_theme_id: ss.sub_theme_id ?? null,
        topic_id: ss.topic_id ?? null,
        no_of_questions: ss.no_of_questions,
        mark_per_question: ss.mark_per_question,
        question_type_id: ss.question_type_id,
        report_theme_id: ss.report_theme_id ?? null,
        blooms_level_id: (ss as any).blooms_level_id ?? null,
        difficult_level_id: (ss as any).difficult_level_id ?? (ss as any).difficulty_level_id ?? null,
        competency_id: (ss as any).competency_id ?? null,
        strand_id: (ss as any).strand_id ?? null,
        base_lang: (ss as any).base_lang ?? null,
        target_lang: (ss as any).target_lang ?? null,
      }))

      await createSubsections({
        resource: 'paper_template_sub_sections',
        values: subsectionValues,
        metaData: paperTemplateSubSectionsMetaData,
      })

      message.success('Combined paper template created successfully.')
      onClose()
      await onCreated?.()
    } catch (e) {
      console.error(e)
      message.error('Failed to create combined paper template.')
    } finally {
      setSubmitting(false)
    }
  }

  const tq = getOption(tqId)
  const skt = getOption(sktId)
  const other = getOption(otherId)
  const total = (tq?.questionCount ?? 0) + (skt?.questionCount ?? 0) + (other?.questionCount ?? 0)

  const disabled = submitting || loadingTemplates || loadingSubsections

  return (
    <Modal
      visible={visible}
      title="Combine Paper Templates"
      onCancel={onClose}
      okText="Create"
      okButtonProps={{ loading: submitting, disabled }}
      onOk={handleCreate}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ name: '' }}>
        <Form.Item
          label="New Paper Template Name"
          name="name"
          rules={[{ required: true, message: 'Please enter a name for the combined template.' }]}
        >
          <Input placeholder="Combined Paper Template Name" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input placeholder="Optional description" />
        </Form.Item>

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Form.Item
            label={`TQ (${REQUIRED_TQ} questions) Paper Template`}
            name="tq_paper_template_id"
            rules={[{ required: true, message: 'Please select a TQ paper template.' }]}
          >
            <Select
              showSearch
              placeholder="Select TQ paper template"
              optionFilterProp="label"
              options={tqOptions.map(o => ({
                value: o.id,
                label: `${o.name} (${o.questionCount}Q)${o.productType ? ` · ${o.productType}` : ''}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={`SKT (${REQUIRED_SKT} questions) Paper Template`}
            name="skt_paper_template_id"
            rules={[{ required: true, message: 'Please select an SKT paper template.' }]}
          >
            <Select
              showSearch
              placeholder="Select SKT paper template"
              optionFilterProp="label"
              options={sktOptions.map(o => ({
                value: o.id,
                disabled: o.questionCount !== REQUIRED_SKT,
                label: `${o.name} (${o.questionCount}Q)${o.productType ? ` · ${o.productType}` : ''}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={`Other (${REQUIRED_OTHER} questions) Paper Template`}
            name="other_paper_template_id"
            rules={[{ required: true, message: 'Please select the Other paper template.' }]}
          >
            <Select
              showSearch
              placeholder="Select Other paper template"
              optionFilterProp="label"
              options={otherOptions.map(o => ({
                value: o.id,
                disabled: o.questionCount !== REQUIRED_OTHER,
                label: `${o.name} (${o.questionCount}Q)${o.productType ? ` · ${o.productType}` : ''}`,
              }))}
            />
          </Form.Item>

          <Typography.Text type={total === REQUIRED_TOTAL ? 'success' : 'danger'}>
            Total questions selected: {total} / {REQUIRED_TOTAL}
          </Typography.Text>
          {!loadingTemplates && !loadingSubsections && templatesList?.data?.length ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
              Templates are listed only if subsection question counts sum to exactly {REQUIRED_TQ} (TQ), {REQUIRED_SKT} (SKT), and{' '}
              {REQUIRED_OTHER} (Other). If a dropdown is empty, fix the source template totals or wait for subsection data to load.
            </Typography.Paragraph>
          ) : null}
        </Space>
      </Form>
    </Modal>
  )
}





